import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { CompanyProfileTabProps } from "./OverviewTab";

const NONE = "-";

interface BankingRow extends DataTableRow {
  bankName: string;
  accountNumber: string;
  currency: string;
  accountType: ReactNode;
  swift: string;
  iban: string;
}

/**
 * The Company Data tab's Banking section — now a Carbon DataTable (bank, account number, currency,
 * account type, SWIFT, IBAN) instead of a card list. `bankIdentifier` and `routingNumber` are
 * omitted on purpose: the backend never populates the former, and the latter is a rarely-used
 * US-only field. The account type is shown as a green Savings/Checking tag, as before.
 */
const BankingTab = ({ companyProfile }: CompanyProfileTabProps) => {
  const { t } = useTranslation();
  const bankingInfos = companyProfile.bankingInfos ?? [];

  const rows: BankingRow[] = bankingInfos.map((bankingInfo, index) => {
    const accountTypeLabel =
      bankingInfo.accountType === "SAVINGS"
        ? t("companyProfile.savingsAccount")
        : t("companyProfile.checkingAccount");

    return {
      id: bankingInfo.id ?? index,
      bankName: bankingInfo.bankName || NONE,
      accountNumber: bankingInfo.accountNumber || NONE,
      currency: bankingInfo.currency || NONE,
      accountType: (
        <Tag type="green" size="sm">
          {accountTypeLabel}
        </Tag>
      ),
      swift: bankingInfo.swift || NONE,
      iban: bankingInfo.iban || NONE,
    };
  });

  const headers: DataTableColumn<BankingRow>[] = [
    { key: "bankName", header: t("companyProfile.table.bank") },
    { key: "accountNumber", header: t("companyProfile.accountNumber") },
    { key: "currency", header: t("companyProfile.currency") },
    { key: "accountType", header: t("companyProfile.accountType") },
    { key: "swift", header: t("companyProfile.swift") },
    { key: "iban", header: t("companyProfile.iban") },
  ];

  return (
    <div className="company-profile__section">
      <h2 className="company-profile__section-heading">
        {t("companyProfile.bankingInformation")}
      </h2>
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        empty={
          <EmptyState
            title={t("companyProfile.table.noBanking")}
            description={t("companyProfile.table.noBankingDesc")}
          />
        }
      />
    </div>
  );
};

export default BankingTab;
